import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/site/Reveal";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const fieldClass =
  "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder:text-white/30 focus:border-gold outline-none transition-colors duration-300";

export default function Apply() {
  const [params] = useSearchParams();
  const position = params.get("position") || "";
  const emptyForm = { position, name: "", email: "", phone: "", message: "", website: "" };
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/applications", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="apply-section" className="bg-ink py-24 md:py-32 min-h-[80vh]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <Reveal>
          <Link to="/recruit" data-testid="apply-back" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-gold transition-colors">
            <ArrowLeft size={16} /> 採用情報に戻る
          </Link>
          <p className="mt-10 font-mono text-xs tracking-[0.25em] uppercase text-gold mb-6">// 07 — Apply / 採用応募</p>
          <h2 className="font-jp text-4xl sm:text-5xl font-bold tracking-tight chrome-text leading-tight">採用応募フォーム</h2>
          {position && (
            <p className="mt-6 text-base font-light text-white/60">
              応募職種: <span className="text-gold font-jp font-bold" data-testid="apply-position-label">{position}</span>
            </p>
          )}
        </Reveal>

        {done ? (
          <Reveal delay={0.1}>
            <div className="mt-14 rounded-2xl border border-gold/30 bg-gold/[0.05] p-10 text-center" data-testid="apply-success">
              <CheckCircle2 size={40} className="mx-auto text-gold" />
              <h3 className="mt-5 font-jp text-2xl font-bold text-white">応募を受け付けました</h3>
              <p className="mt-4 text-sm font-light leading-relaxed text-white/60">
                ご応募ありがとうございます。内容を確認のうえ、担当者よりご連絡いたします。
              </p>
              <Link to="/recruit" className="mt-8 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm text-gold hover:bg-gold hover:text-ink transition-colors duration-300">
                採用情報に戻る
              </Link>
            </div>
          </Reveal>
        ) : (
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="apply-form" className="mt-14 space-y-8">
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
              <input type="hidden" value={form.position} />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <input data-testid="apply-name" className={fieldClass} placeholder="お名前 *" value={form.name} onChange={set("name")} required />
                <input data-testid="apply-email" type="email" className={fieldClass} placeholder="メールアドレス *" value={form.email} onChange={set("email")} required />
              </div>
              <input data-testid="apply-phone" type="tel" className={fieldClass} placeholder="電話番号（任意）" value={form.phone} onChange={set("phone")} />
              <textarea data-testid="apply-message" rows={5} className={`${fieldClass} resize-none`} placeholder="自己PR・ご質問など *" value={form.message} onChange={set("message")} required />
              <button type="submit" data-testid="apply-submit" disabled={loading} className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-medium text-ink hover:bg-gold-light glow-cyan transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                応募する
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        )}
      </div>
    </section>
  );
}
